import {
	ReactElement,
	KeyboardEvent,
	Ref
} from "react";
import {
	ActionAndOthers,
	ActionGroup,
	ActionKind,
	resolveActionTitle
} from "client_legacy/ndaw/actions";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import { ActionListBusyIndicator } from "./ActionListBusyIndicator";
import { ActionListCheckbox } from "./ActionListCheckbox";
import { ActionListDivider } from "./ActionListDivider";
import { ActionListItem } from "./ActionListItem";

type FocusableItemProps = {
	ref?: Ref<HTMLLIElement>;
	onKeyDown?: (args: KeyboardEvent<HTMLLIElement>) => void;
	tabIndex?: number;
};

export function actionComponentFactory<TContext>(
	action: ActionAndOthers<TContext>,
	context: TContext,
	itemProps: FocusableItemProps = {}
): ReactElement {
	switch (action.kind) {
		case ActionKind.Regular:
			return (
				<ActionListItem
					ref={itemProps.ref}
					onKeyDown={itemProps.onKeyDown}
					tabIndex={itemProps.tabIndex}
					key={action.name}
					trackingId={action.name}
					icon={action.icon}
					text={resolveActionTitle(action.title, context)}
					onClick={() => action.execute(context)}
					shouldNotAutoCloseContainingDropOver={action.closeMenuOnClick === false}
				/>
			);
		case ActionKind.MultiSelectOption:
			return (
				<ActionListCheckbox
					ref={itemProps.ref}
					onKeyDown={itemProps.onKeyDown}
					tabIndex={itemProps.tabIndex}
					key={action.name}
					trackingId={action.name}
					text={resolveActionTitle(action.title, context)}
					value={
						typeof action.isChecked === "function"
							? action.isChecked(context)
							: action.isChecked ?? false
					}
					isDisabled={action.isDisabled}
					shouldNotAutoCloseContainingDropOver={action.closeMenuOnClick === false}
					icon={action.icon}
					onChange={() => action.execute(context)}
				/>
			);
		case ActionKind.SelectOption:
			const isSelected: boolean = typeof action.isChecked === "function"
				? action.isChecked(context)
				: action.isChecked ?? false;

			return (
				<ActionListItem
					ref={itemProps.ref}
					onKeyDown={itemProps.onKeyDown}
					tabIndex={itemProps.tabIndex}
					key={action.name}
					trackingId={action.name}
					text={resolveActionTitle(action.title, context)}
					isActive={isSelected}
					rightIcon={isSelected ? "check" : undefined}
					isDisabled={action.isDisabled}
					shouldNotAutoCloseContainingDropOver={action.closeMenuOnClick === false}
					icon={action.icon}
					onClick={() => action.execute(context)}
				/>
			);
		case ActionKind.Divider:
			return <ActionListDivider key={action.name} />;
		case ActionKind.BusyIndicator:
			return <ActionListBusyIndicator key={action.name} />;
		default:
			never(action);
	}
}

export function computeActionsToRender<TContext>(
	actions: readonly ActionAndOthers<TContext>[],
	context: TContext,
	showVirtualGroupDividers = false
): ActionAndOthers<TContext>[] {
	const result: ActionAndOthers<TContext>[] = [];

	let lastGroup: ActionGroup | undefined = undefined;
	for (const action of actions) {

		if (!action.canExecute(context)) {
			continue;
		}

		const groupKind: "regular" | "alternative" | "virtual" | undefined = action.group
			?.kind;

		const group: ActionGroup | undefined = !showVirtualGroupDividers && groupKind === "virtual"
			? undefined
			: action.group;

		const groupName: string | undefined = group?.name;
		const lastGroupName: string | undefined = lastGroup?.name;

		if (groupName !== lastGroupName) {
			if (result.length > 0) {
				result.push({
					kind: ActionKind.Divider,
					group: (group ?? lastGroup)!,
					name: newGuid(),
					canExecute: () => true
				});
			}
			lastGroup = group;
		}

		result.push(action);
	}

	return result;
}